import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { ArrowRight, CheckCircle2, AlertTriangle, AlertCircle, Info } from 'lucide-react';

export function ConfidenceRouter() {
  const { toast } = useToast();
  const [highThreshold, setHighThreshold] = useState(90);
  const [mediumThreshold, setMediumThreshold] = useState(85);
  const [accountNumberConf, setAccountNumberConf] = useState(95);
  const [billDateConf, setBillDateConf] = useState(90);
  const [amountDueConf, setAmountDueConf] = useState(90);
  const [meterNumberConf, setMeterNumberConf] = useState(70);
  const [consumptionConf, setConsumptionConf] = useState(80);

  const handleSave = () => {
    toast({
      title: "Routing rules saved",
      description: "Confidence-based routing has been configured successfully.",
    });
  };

  return (
    <div className="space-y-4">
      <Alert className="border-primary/20 bg-primary/5">
        <Info className="h-4 w-4 text-primary" />
        <AlertTitle>New Parsing Workflow</AlertTitle>
        <AlertDescription>
          <ol className="list-decimal list-inside space-y-1 mt-2">
            <li><strong>Vendor API First:</strong> Bills are checked against vendor API configurations</li>
            <li><strong>Manual Trigger:</strong> If no API or API fails, bills wait for manual action</li>
            <li><strong>AI Parsing:</strong> Users can manually trigger AI parsing from the queue</li>
            <li><strong>Confidence Routing:</strong> After AI parsing, bills route based on confidence scores below</li>
          </ol>
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Confidence-Based Auto-Routing</CardTitle>
          <CardDescription>
            Configure how bills are automatically routed based on AI confidence scores
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border p-4 bg-muted/50">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-medium">High Confidence</div>
                  <Badge variant="default">≥ {highThreshold}%</Badge>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Threshold: {highThreshold}%</Label>
                  <Slider
                    value={[highThreshold]}
                    onValueChange={(v) => setHighThreshold(v[0])}
                    min={80}
                    max={100}
                    step={1}
                  />
                </div>
                <div className="text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 mt-2">
                    <ArrowRight className="h-4 w-4" />
                    <span>Action: Auto-move to Validation Queue</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border p-4 bg-muted/50">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Medium Confidence</div>
                  <Badge variant="secondary">{mediumThreshold}% - {highThreshold - 1}%</Badge>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Lower Threshold: {mediumThreshold}%</Label>
                  <Slider
                    value={[mediumThreshold]}
                    onValueChange={(v) => setMediumThreshold(v[0])}
                    min={70}
                    max={90}
                    step={1}
                  />
                </div>
                <div className="text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 mt-2">
                    <ArrowRight className="h-4 w-4" />
                    <span>Action: Move to Validation with warning flags</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border p-4 bg-muted/50">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Low Confidence</div>
                  <Badge variant="destructive">&lt; {mediumThreshold}%</Badge>
                </div>
                <div className="text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 mt-2">
                    <ArrowRight className="h-4 w-4" />
                    <span>Action: Send to Manual Review Queue (High Priority)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Per-Field Confidence Thresholds</CardTitle>
          <CardDescription>
            Set minimum confidence required for critical fields
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Account Number: {accountNumberConf}%</Label>
            <Slider
              value={[accountNumberConf]}
              onValueChange={(v) => setAccountNumberConf(v[0])}
              min={70}
              max={100}
              step={1}
            />
            <p className="text-xs text-muted-foreground">Critical field - requires high confidence</p>
          </div>

          <div className="space-y-2">
            <Label>Bill Date: {billDateConf}%</Label>
            <Slider
              value={[billDateConf]}
              onValueChange={(v) => setBillDateConf(v[0])}
              min={70}
              max={100}
              step={1}
            />
          </div>

          <div className="space-y-2">
            <Label>Amount Due: {amountDueConf}%</Label>
            <Slider
              value={[amountDueConf]}
              onValueChange={(v) => setAmountDueConf(v[0])}
              min={70}
              max={100}
              step={1}
            />
          </div>

          <div className="space-y-2">
            <Label>Meter Number: {meterNumberConf}%</Label>
            <Slider
              value={[meterNumberConf]}
              onValueChange={(v) => setMeterNumberConf(v[0])}
              min={50}
              max={100}
              step={1}
            />
            <p className="text-xs text-muted-foreground">Optional field - lower confidence acceptable</p>
          </div>

          <div className="space-y-2">
            <Label>Consumption: {consumptionConf}%</Label>
            <Slider
              value={[consumptionConf]}
              onValueChange={(v) => setConsumptionConf(v[0])}
              min={50}
              max={100}
              step={1}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave}>Save Routing Rules</Button>
      </div>
    </div>
  );
}
